import { useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import Datatable from "../../../core/common/dataTable";
import ExportExcel from "../../../core/common/exportExcel";
import useApi from "../../../API/useApi";
import { candidate_portal } from "../../../environment";

interface TopSubmissionRecord {
    key: string;
    timestamp: string;
    groupParticipantNames: string;
    groupNo: string;
    problemStatement: string;
    formatType: string;
    uploadPresentation: string;
}

interface ApiGroupSubmission {
    group_id: number;
    problem_stmt: string | null;
    format_type: string | null;
    ppt_file_path: string | null; // sometimes the literal string "null" instead of actual null
    status?: number;
    created_on: string | null;
    last_updated_on?: string | null;
    student_ids: string[] | null;
}

function formatTimestamp(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

function formatParticipantNames(names: string[] | null | undefined): string {
    if (!names || names.length === 0) return "";
    return names.join(", ");
}

// Check for null/"null" on the RAW path first, then build the full URL.
function formatFilePath(path: string | null | undefined, basePath: string): string {
    if (!path || path === "null") return "";
    return `${basePath}${path}`;
}

function mapSubmissionToRecord(submission: ApiGroupSubmission): TopSubmissionRecord {
    return {
        key: String(submission.group_id),
        timestamp: formatTimestamp(submission.created_on),
        groupParticipantNames: formatParticipantNames(submission.student_ids),
        groupNo: String(submission.group_id),
        problemStatement: submission.problem_stmt ?? "",
        formatType: submission.format_type ?? "",
        uploadPresentation: formatFilePath(submission.ppt_file_path, candidate_portal),
    };
}

/* ------------------------------------------------------------------ */
/* Charts                                                              */
/* ------------------------------------------------------------------ */

// Palette taken from the app's green theme (sidebar active item / Export button).
const THEME_GREEN = "#16a34a";
const PIE_COLORS = [
    "#16a34a",
    "#4ade80",
    "#15803d",
    "#86efac",
    "#14532d",
    "#bbf7d0",
    "#22c55e",
    "#065f46",
];
const TITLE_COLOR = "#1b2850"; // same navy as the page heading

interface Slice {
    label: string;
    count: number;
}

/** Count how often each (non-empty) value appears in a column. */
function countBy(
    records: TopSubmissionRecord[],
    field: keyof TopSubmissionRecord
): Slice[] {
    const map = new Map<string, number>();
    records.forEach((r) => {
        const v = String(r[field] ?? "").trim();
        if (!v) return;
        map.set(v, (map.get(v) ?? 0) + 1);
    });
    return Array.from(map.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
}

function ChartCard({
    title,
    total,
    unit = "responses",
    children,
}: {
    title: string;
    total: number;
    unit?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="card shadow-sm border-0 rounded-4 h-100">
            <div className="card-body">
                <h6
                    className="mb-1"
                    style={{ color: TITLE_COLOR, fontWeight: 600, lineHeight: 1.35 }}
                >
                    {title}
                </h6>
                <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                    {total} {unit}
                </div>
                {total === 0 ? (
                    <div
                        className="text-muted d-flex align-items-center justify-content-center"
                        style={{ height: 200, fontSize: 13 }}
                    >
                        No data available
                    </div>
                ) : (
                    children
                )}
            </div>
        </div>
    );
}

function PieChart({ data }: { data: Slice[] }) {
    const total = data.reduce((s, d) => s + d.count, 0);
    const R = 80;
    const C = 90;

    let angle = -Math.PI / 2;
    const slices = data.map((d, i) => {
        const frac = total ? d.count / total : 0;
        const start = angle;
        const end = angle + frac * Math.PI * 2;
        angle = end;
        const large = end - start > Math.PI ? 1 : 0;
        const x1 = C + R * Math.cos(start);
        const y1 = C + R * Math.sin(start);
        const x2 = C + R * Math.cos(end);
        const y2 = C + R * Math.sin(end);
        const mid = (start + end) / 2;
        return {
            ...d,
            frac,
            color: PIE_COLORS[i % PIE_COLORS.length],
            path:
                frac >= 0.9999
                    ? null
                    : `M${C},${C} L${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2} Z`,
            lx: C + R * 0.62 * Math.cos(mid),
            ly: C + R * 0.62 * Math.sin(mid),
        };
    });

    return (
        <div className="d-flex align-items-center flex-wrap gap-3">
            <svg viewBox="0 0 180 180" width={190} height={190} role="img">
                {slices.map((s) =>
                    s.path ? (
                        <path key={s.label} d={s.path} fill={s.color} stroke="#fff" strokeWidth={1.5}>
                            <title>{`${s.label}: ${s.count} (${Math.round(s.frac * 100)}%)`}</title>
                        </path>
                    ) : (
                        <circle key={s.label} cx={C} cy={C} r={R} fill={s.color} />
                    )
                )}
                {slices.map(
                    (s) =>
                        s.frac >= 0.06 && (
                            <text
                                key={`t-${s.label}`}
                                x={s.lx}
                                y={s.ly + 4}
                                textAnchor="middle"
                                fontSize="11"
                                fontWeight={600}
                                fill={
                                    ["#bbf7d0", "#86efac", "#4ade80"].includes(s.color)
                                        ? "#14532d"
                                        : "#fff"
                                }
                            >
                                {Math.round(s.frac * 100)}%
                            </text>
                        )
                )}
            </svg>

            <ul className="list-unstyled mb-0" style={{ fontSize: 13, minWidth: 140 }}>
                {slices.map((s) => (
                    <li key={s.label} className="d-flex align-items-center gap-2 mb-1">
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background: s.color,
                                display: "inline-block",
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ color: "#374151" }}>
                            {s.label} <span className="text-muted">({s.count})</span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const columns: ColumnsType<TopSubmissionRecord> = [
    {
        title: "Timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        width: 160,
    },
    {
        title: "Group participant names",
        dataIndex: "groupParticipantNames",
        key: "groupParticipantNames",
        width: 300,
        ellipsis: true,
    },
    {
        title: "Group No",
        dataIndex: "groupNo",
        key: "groupNo",
        width: 100,
    },
    {
        title: "Problem statement in one line",
        dataIndex: "problemStatement",
        key: "problemStatement",
        width: 280,
        ellipsis: true,
    },
    {
        title: "Type of format",
        dataIndex: "formatType",
        key: "formatType",
        width: 180,
        ellipsis: true,
    },
    {
        title: "Upload Presentation",
        dataIndex: "uploadPresentation",
        key: "uploadPresentation",
        width: 260,
        ellipsis: true,
        // Custom flag read by ExportExcel to write this column as a real hyperlink cell.
        isLink: true,
        render: (text: string) => {
            if (!text) return "-";
            return (
                <a href={text} target="_blank" rel="noopener noreferrer">
                    View Presentation
                </a>
            );
        },
    } as any, // antd's ColumnsType doesn't know about `isLink`; see note below
];

export default function TopSubmissions() {
    const { data, loading } = useApi("/get-top-submission-report", {
        method: "GET",
        autoCall: true,
    });

    const tableData = useMemo<TopSubmissionRecord[]>(() => {
        const submissions: ApiGroupSubmission[] = data?.data ?? [];
        return submissions.map(mapSubmissionToRecord);
    }, [data]);

    const formatSlices = useMemo(() => countBy(tableData, "formatType"), [tableData]);

    const uploadSlices = useMemo<Slice[]>(() => {
        const uploaded = tableData.filter((r) => r.uploadPresentation).length;
        const notUploaded = tableData.length - uploaded;
        return [
            { label: "Presentation uploaded", count: uploaded },
            { label: "Not uploaded yet", count: notUploaded },
        ].filter((s) => s.count > 0);
    }, [tableData]);

    const sum = (s: Slice[]) => s.reduce((a, b) => a + b.count, 0);

    return (
        <div className="page-wrapper">
            <div className="content page-enter">

                {/* ---------- Page title ---------- */}
                <div
                    className="card shadow-sm border-0 rounded-4 mb-3"
                    style={{ borderLeft: `5px solid ${THEME_GREEN}`, overflow: "hidden" }}
                >
                    <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 14,
                                    background: "#dcfce7",
                                    color: THEME_GREEN,
                                    flexShrink: 0,
                                }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="mb-0" style={{ fontSize: "1.6rem", fontWeight: 700, color: TITLE_COLOR, letterSpacing: "-0.01em" }}>
                                    TOP Submission Reports
                                </h1>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    Group problem statements and presentations submitted by participants
                                </div>
                            </div>
                        </div>

                        <span style={{ background: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: 13, padding: "6px 14px", borderRadius: 999 }}>
                            {loading ? "Loading…" : `${tableData.length} groups`}
                        </span>
                    </div>
                </div>

                {/* ---------- Charts ---------- */}
                {!loading && (
                    <div className="row g-3 mb-3 stagger-children">
                        <div className="col-12 col-lg-6">
                            <ChartCard title="Type of format" total={sum(formatSlices)} unit="groups">
                                <PieChart data={formatSlices} />
                            </ChartCard>
                        </div>
                        <div className="col-12 col-lg-6">
                            <ChartCard title="Presentation upload status" total={sum(uploadSlices)} unit="groups">
                                <PieChart data={uploadSlices} />
                            </ChartCard>
                        </div>
                    </div>
                )}

                {/* ---------- Table (export lives in the table header) ---------- */}
                <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-header bg-transparent d-flex align-items-center justify-content-between flex-wrap gap-2 pt-3 pb-3">
                        <h5 className="mb-0" style={{ color: TITLE_COLOR, fontWeight: 600 }}>
                            All submissions
                        </h5>

                        <ExportExcel
                            columns={columns}
                            dataSource={tableData}
                            fileName="top_submission_reports"
                            buttonLabel="Export to Excel"
                        />
                    </div>
                    <div className="card-body p-0">
                        <Datatable
                            columns={columns}
                            dataSource={tableData}
                            loading={loading}
                        // rowKey="timestamp"
                        // scroll={{ x: 2500 }}
                        />
                    </div>
                </div>

            </div>
        </div>
    )
}